import { useCallback, useEffect, useState } from "preact/hooks";

import { fetchWeather } from "../lib/api";
import type { WeatherOut } from "../types";

/**
 * Sprint-009 B5 — the Dashboard weather card + Today-header strip's shared
 * data hook (design-phase-b.md §3, ADR-002 Amendment 5 decision 4). One
 * `GET /api/v1/weather` fetch, cache-only server-side, all authenticated
 * roles — no `enabled`/role gate needed (contrast `useProjects`/
 * `useActivity`, whose fetches are genuinely role-restricted).
 * `weather.available`/`weather.stale` drive the card's own state machine
 * (§3.3) rather than this hook collapsing them into a client-side error —
 * the endpoint itself never 5xxs on an upstream failure (§4.7), so a
 * thrown/caught error here means a genuine network/app failure, not a
 * degraded-but-successful weather response.
 */
export function useWeather() {
  const [weather, setWeather] = useState<WeatherOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchWeather();
      setWeather(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the forecast.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { weather, loading, error, refresh };
}
